/**
 * Persist affiliate referral code (?ref=) across signup/auth redirects.
 * First-touch attribution: do not overwrite an existing stored ref until it expires.
 */

const AFFILIATE_REF_STORAGE_KEY = "bomani.affiliateRef";
const AFFILIATE_VISIT_PENDING_KEY = "bomani.affiliateVisitPending";
const AFFILIATE_REF_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

export type AffiliateRefPayload = {
  affiliateCode: string;
  capturedAt: string;
};

function isBrowserStorageAvailable(): boolean {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

export function readAffiliateRefFromStorage(): string | undefined {
  if (!isBrowserStorageAvailable()) return undefined;
  try {
    const raw = localStorage.getItem(AFFILIATE_REF_STORAGE_KEY);
    if (!raw?.trim()) return undefined;
    const parsed = JSON.parse(raw) as AffiliateRefPayload & { expiresAt?: string };
    if (
      typeof parsed.affiliateCode !== "string" ||
      !parsed.affiliateCode.trim()
    ) {
      return undefined;
    }
    if (typeof parsed.capturedAt === "string" && parsed.capturedAt) {
      const capturedMs = Date.parse(parsed.capturedAt);
      if (
        Number.isFinite(capturedMs) &&
        Date.now() - capturedMs > AFFILIATE_REF_TTL_MS
      ) {
        clearAffiliateRefFromStorage();
        return undefined;
      }
    }
    return parsed.affiliateCode.trim();
  } catch {
    return undefined;
  }
}

/**
 * Store referral code if none is already stored (first-touch).
 * Pass `force: true` only when intentionally replacing.
 */
export function writeAffiliateRefToStorage(
  affiliateCode: string,
  options?: { force?: boolean },
): void {
  if (!isBrowserStorageAvailable()) return;
  const trimmed = affiliateCode.trim();
  if (!trimmed) return;

  if (!options?.force && readAffiliateRefFromStorage()) {
    return;
  }

  try {
    const payload: AffiliateRefPayload = {
      affiliateCode: trimmed,
      capturedAt: new Date().toISOString(),
    };
    localStorage.setItem(AFFILIATE_REF_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearAffiliateRefFromStorage(): void {
  if (!isBrowserStorageAvailable()) return;
  try {
    localStorage.removeItem(AFFILIATE_REF_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  clearAffiliateVisitPending();
}

/**
 * Mark that a visit still needs recording on the next sign-in call
 * (e.g. user opened /signup?ref= without going through landing-page).
 */
export function markAffiliateVisitPending(): void {
  if (!isBrowserStorageAvailable()) return;
  try {
    localStorage.setItem(AFFILIATE_VISIT_PENDING_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** Consume-and-clear the pending visit flag for the sign-in request. */
export function consumeAffiliateVisitPending(): boolean {
  if (!isBrowserStorageAvailable()) return false;
  try {
    const pending = localStorage.getItem(AFFILIATE_VISIT_PENDING_KEY) === "1";
    if (pending) {
      localStorage.removeItem(AFFILIATE_VISIT_PENDING_KEY);
    }
    return pending;
  } catch {
    return false;
  }
}

export function clearAffiliateVisitPending(): void {
  if (!isBrowserStorageAvailable()) return;
  try {
    localStorage.removeItem(AFFILIATE_VISIT_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Capture ?ref= from a request URL into localStorage (first-touch).
 * Returns the ref from the URL when present (even if storage already had a code).
 */
export function captureAffiliateRefFromUrl(url: string | URL): string | undefined {
  try {
    const parsed = typeof url === "string" ? new URL(url) : url;
    const ref = parsed.searchParams.get("ref")?.trim();
    if (!ref) return undefined;
    writeAffiliateRefToStorage(ref);
    return ref;
  } catch {
    return undefined;
  }
}
